import { useEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import CommandBar from "./components/CommandBar";
import { ArtistGate } from "./components/gate/ArtistGate";
import LoadingStateManager from "./components/Loading/LoadingStateManager";
import { MailingListPopup } from "./components/mailing/MailingListPopup";
import { useViewerUnlock } from "./components/mailing/useViewerUnlock";
import { FanHubMount } from "./components/templates/ArtistTemplate/FanHubMount";
import { UpdateNotification } from "./components/UpdateNotification";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { useAnalyticsTracker } from "./hooks/useAnalytics";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { usePlaylist } from "./contexts/PlaylistContext";
import { registerAssetFailure, withCacheBust } from "./utils/apiCache";
import { shouldIgnoreGlobalShortcut } from "./utils/shortcutGuard";
import { scrollForNavigation } from "./lib/viewTransitions";

function App() {
  const location = useLocation();
  const { closeAllPanels } = usePlaylist();
  useAnalyticsTracker();
  useViewerUnlock();
  const { showUpdatePrompt, isUpdating, acceptUpdate, dismissUpdate } = useAppUpdate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || shouldIgnoreGlobalShortcut(e)) return;
      closeAllPanels();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeAllPanels]);

  const navigationType = useNavigationType();
  // A new page starts at the top; back and forward return to where the
  // visitor was. The view transition controller keeps the positions.
  useEffect(() => {
    scrollForNavigation(location, navigationType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleAssetError = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement) || !target.currentSrc) return;
      if (registerAssetFailure(target.currentSrc)) {
        target.src = withCacheBust(target.currentSrc);
      }
    };

    window.addEventListener("error", handleAssetError, true);
    return () => window.removeEventListener("error", handleAssetError, true);
  }, []);

  return (
    <LoadingStateManager>
      <ArtistGate>
        <Outlet />
      </ArtistGate>
      <FanHubMount />
      <MailingListPopup />
      <CommandBar />
      <InstallPrompt />
      <UpdateNotification
        isVisible={showUpdatePrompt}
        isUpdating={isUpdating}
        onAccept={acceptUpdate}
        onDismiss={dismissUpdate}
      />
    </LoadingStateManager>
  );
}

export default App;
